import React, { FC, useContext, useMemo } from 'react';
import styles from './index.module.scss';
import { EchartsWithTheme, Empty, useTheme } from '@maxtropy/components';
import { Spin } from 'antd';
import { useRequest } from 'ahooks';
import dayjs from 'dayjs';
import { apiV2PaintingDashboardTypeRatioPost } from '@maxtropy/device-customer-apis-v2';
import { keepOneAndWanOrLine, keepOneOrLine } from '@/utils/utils';
import { DateContext } from '../../../index';
import { IndexTimeActive } from '../../SwitchDate';
import { getHighEnergyPieChartOption } from './charts';
import CustomTooltip from '../../CustomTooltip';

const COLORS = ['#2D8DFF', '#FF477B', '#FFCB47', '#9B59B6', '#16DD8E', '#52E7FF'];

const HighEnergyPie: FC = () => {
  const theme = useTheme();
  const { activeKey, selectedDate } = useContext(DateContext);

  // 构建请求参数
  const requestParams = useMemo(() => {
    const timeResolution = activeKey === IndexTimeActive.THIS_MONTH ? 'M' : 'Y';
    const unit = activeKey === IndexTimeActive.THIS_MONTH ? 'month' : 'year';
    const startTime = selectedDate.startOf(unit).valueOf();
    // 结束时间：前一天23:59:59（如果是当前月份/年份），否则为月末/年末
    const isCurrentPeriod =
      selectedDate.format(activeKey === IndexTimeActive.THIS_MONTH ? 'YYYY-MM' : 'YYYY') ===
      dayjs().format(activeKey === IndexTimeActive.THIS_MONTH ? 'YYYY-MM' : 'YYYY');
    const endTime = isCurrentPeriod
      ? dayjs().subtract(1, 'day').endOf('day').valueOf()
      : selectedDate.endOf(unit).valueOf();
    return { startTime, endTime, timeResolution };
  }, [activeKey, selectedDate]);

  const { data, loading } = useRequest(() => apiV2PaintingDashboardTypeRatioPost(requestParams), {
    refreshDeps: [requestParams],
  });

  // 转换数据格式
  const pieData = useMemo(() => {
    if (!data?.items?.length) return [];
    return data.items.map((item, i) => ({
      name: item.typeName ?? '--',
      value: item.consumption ?? 0,
      rate: item.ratio ?? 0,
      cost: keepOneAndWanOrLine(item.consumption) + 'kWh',
      color: COLORS[i % COLORS.length],
    }));
  }, [data]);

  const totalConsumption = useMemo(() => {
    return keepOneAndWanOrLine(data?.totalConsumption);
  }, [data]);

  const pieChartOption = useMemo(() => {
    if (!pieData.length) return null;
    return getHighEnergyPieChartOption(pieData, totalConsumption);
  }, [pieData, totalConsumption]);

  const legendNameWidth = useMemo(() => {
    const maxLen = Math.max(...pieData.map(item => item.name.length), 0);
    return Math.min(maxLen * 14, 100);
  }, [pieData]);

  // 计算最高/最低占比
  const ratioStats = useMemo(() => {
    if (!pieData.length) return { highest: null, lowest: null };
    const sorted = [...pieData].sort((a, b) => b.rate - a.rate);
    return {
      highest: sorted[0],
      lowest: sorted[sorted.length - 1],
    };
  }, [pieData]);

  if (loading) {
    return (
      <div className={styles.loading_wrapper}>
        <Spin />
      </div>
    );
  }

  if (!pieData.length) {
    return <Empty />;
  }

  return (
    <>
      <div className={styles.pie_content}>
        <div className={styles.pie_chart_wrapper}>
          <EchartsWithTheme
            type="pie"
            theme={theme}
            style={{ height: '100%', width: '100%' }}
            option={pieChartOption}
            empty={{
              isShowEmpty: !pieData.length,
              emptyNode: <Empty />,
            }}
          />
        </div>
        <div className={styles.pie_legend} style={{ maxHeight: pieData.length > 6 ? '180px' : 'auto' }}>
          {pieData.map((item, index) => (
            <div className={styles.legend_item} key={item.name ?? index}>
              <div className={styles.legend_icon} style={{ backgroundColor: item.color }} />
              <CustomTooltip title={item.name}>
                <div className={styles.legend_name} style={{ width: legendNameWidth }}>
                  {item.name}
                </div>
              </CustomTooltip>
              <CustomTooltip title={item.cost}>
                <div className={styles.legend_value}>{item.cost}</div>
              </CustomTooltip>
              <CustomTooltip title={`${keepOneOrLine(item.rate)}%`}>
                <div className={styles.legend_rate}>{keepOneOrLine(item.rate)}%</div>
              </CustomTooltip>
            </div>
          ))}
        </div>
      </div>
      <div className={styles.ratio_tips}>
        <div className={styles.ratio_tip_item}>
          <span className={styles.ratio_tip_label}>最高占比：</span>
          <CustomTooltip
            title={`${ratioStats.highest?.name} (${keepOneOrLine(
              ratioStats.highest?.rate ?? 0
            )}%) ${keepOneAndWanOrLine(ratioStats.highest?.value ?? 0)}kWh`}
          >
            <span className={styles.ratio_tip_value_high}>
              {ratioStats.highest?.name} ({keepOneOrLine(ratioStats.highest?.rate ?? 0)}%){' '}
              {keepOneAndWanOrLine(ratioStats.highest?.value ?? 0)}kWh
            </span>
          </CustomTooltip>
        </div>
        <div className={styles.ratio_tip_divider} />
        <div className={styles.ratio_tip_item}>
          <span className={styles.ratio_tip_label}>最低占比：</span>
          <CustomTooltip
            title={`${ratioStats.lowest?.name} (${keepOneOrLine(ratioStats.lowest?.rate ?? 0)}%) ${keepOneAndWanOrLine(
              ratioStats.lowest?.value ?? 0
            )}kWh`}
          >
            <span className={styles.ratio_tip_value_low}>
              {ratioStats.lowest?.name} ({keepOneOrLine(ratioStats.lowest?.rate ?? 0)}%){' '}
              {keepOneAndWanOrLine(ratioStats.lowest?.value ?? 0)}kWh
            </span>
          </CustomTooltip>
        </div>
      </div>
    </>
  );
};

export default HighEnergyPie;
